import { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { getSuppressionReasonDetails } from "constants/emailSuppression";
import { EmailSuppressionStatus } from "types/Models/Chat";
import { pluralize } from "utils/pluralize";

dayjs.extend(relativeTime);

type SuppressedEmailsModalProps = {
  open: boolean;
  suppressions: EmailSuppressionStatus[];
  isReactivating: boolean;
  onClose: () => void;
  onReactivate: (selected: EmailSuppressionStatus[]) => void;
};

export const SuppressedEmailsModal = ({
  open,
  suppressions,
  isReactivating,
  onClose,
  onReactivate,
}: SuppressedEmailsModalProps) => {
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

  const title = pluralize(
    suppressions.length,
    "Suppressed email address",
    "Suppressed email addresses",
    false
  );

  const handleToggle = (email: string) => {
    const next = new Set(selectedEmails);
    if (next.has(email)) {
      next.delete(email);
    } else {
      next.add(email);
    }
    setSelectedEmails(next);
  };

  const handleReactivateClick = () => {
    const selected = suppressions.filter((suppression) =>
      selectedEmails.has(suppression.email)
    );
    onReactivate(selected);
  };

  return (
    <Modal.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-space-y-1.5">
            {suppressions.map((suppression) => {
              const reason = getSuppressionReasonDetails(
                suppression.suppression_reason
              );

              return (
                <div
                  key={suppression.email}
                  className="t-flex t-items-center t-gap-3 t-rounded-md t-bg-surface-grey t-px-3 t-py-2"
                >
                  <Checkbox
                    name={suppression.email}
                    checked={selectedEmails.has(suppression.email)}
                    disabled={isReactivating}
                    onChange={() => handleToggle(suppression.email)}
                  />
                  <div className="t-flex t-flex-col t-gap-1 t-min-w-0">
                    <span className="t-text-body-sm t-text-text-60 t-truncate">
                      {suppression.email}
                    </span>
                    <div className="t-flex t-items-center t-gap-2">
                      <Tag tagType={reason.tagType} size="small">
                        {reason.label}
                      </Tag>
                      {suppression.suppressed_at && (
                        <span className="t-text-body-sm t-text-text-30">
                          {dayjs(suppression.suppressed_at).fromNow()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button onClick={onClose} disabled={isReactivating}>
            Close
          </Button>
          <Button
            customType="primary"
            disabled={selectedEmails.size === 0 || isReactivating}
            isLoading={isReactivating}
            onClick={handleReactivateClick}
          >
            Reactivate
            {selectedEmails.size > 0 ? ` (${selectedEmails.size})` : ""}
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
